/**
 * Market Monitor widget registrations — side-effect module.
 *
 * Importing this module registers every Market-Monitor widget on the
 * marketMonitorWidgetRegistry. Imported once when the Market Monitor
 * page loads.
 *
 * Amendment A (2026-05-02): each chart is its own widget. The chart-row
 * container is gone.
 */

import { marketMonitorWidgetRegistry } from './registry';
import { ChartWidget } from './ChartWidget';
import { CHART_WIDGET_TYPE } from './chartConfig';
import { ExcelWidget } from './ExcelWidget';
import { EXCEL_WIDGET_TYPE } from './excelConfig';

export { marketMonitorWidgetRegistry } from './registry';
export {
  CHART_WIDGET_TYPE,
  createInitialChartConfig,
  isChartConfig,
  type ChartWidgetConfig,
} from './chartConfig';
export {
  ChartControllerProviderContext,
  useChartController,
  type ChartController,
  type ChartControllerProvider,
} from './ChartController';
export { ChartWidget } from './ChartWidget';
export {
  EXCEL_WIDGET_TYPE,
  MAX_EXCEL_COLS,
  MAX_EXCEL_ROWS,
  MIN_DECIMAL_DIGITS,
  MAX_DECIMAL_DIGITS,
  DEFAULT_EXCEL_COLS,
  DEFAULT_EXCEL_ROWS,
  applyCellPatch,
  appendCol,
  appendRow,
  clearCells,
  createInitialExcelConfig,
  dropCol,
  dropRow,
  growGrid,
  isDslCell,
  isExcelCellNumberFormat,
  isExcelConfig,
  isSheetEmpty,
  parseCellAddress,
  promptOf,
  setCellRaw,
  setCellResolvedDsl,
  setCellsConditionalFormatting,
  setCellsFormat,
  setCellsStyle,
  sheetCellMap,
  writeSheetCells,
  type CellCoord,
  type ExcelCell,
  type ExcelCellNumberFormat,
  type ExcelCellStyle,
  type ExcelWidgetConfig,
} from './excelConfig';
export {
  buildTranslationRequest,
  composeCellDsl,
  formulaMode,
  maskTranslation,
  parseFormulaBody,
  tokenizeFormula,
  type CellExpression,
  type CellRef,
  type FormulaError,
  type FormulaMode,
  type InlinedSpan,
  type ParsedFormula,
} from './excelFormula';
export {
  CF_OPS,
  MAX_CF_AND_CONDITIONS_PER_GROUP,
  MAX_CF_OR_GROUPS_PER_RULE,
  MAX_CF_RULES_PER_CELL,
  MAX_CF_RULE_ID_LEN,
  evaluateRules,
  isCfRule,
  memoEvaluateRules,
  type CfActiveStyle,
  type CfCondition,
  type CfOp,
  type CfRule,
} from './conditionalFormatting';
export {
  ExcelControllerProviderContext,
  useExcelController,
  type ExcelController,
  type ExcelControllerProvider,
} from './ExcelController';
export { ExcelWidget } from './ExcelWidget';

marketMonitorWidgetRegistry.register({
  widgetType: CHART_WIDGET_TYPE,
  displayName: 'Chart',
  description: 'A single DSL chart with editable title, period, and tags',
  icon: 'line-chart',
  component: ChartWidget,
  defaultSize: { width: 6, height: 16 },
  minSize: { width: 5, height: 12 },
  hideShellTitleBar: true,
  hideShellRemoveButton: true,
});

marketMonitorWidgetRegistry.register({
  widgetType: EXCEL_WIDGET_TYPE,
  displayName: 'Excel',
  description:
    'Spreadsheet of DSL cells; each "=" cell evaluates to its last value',
  icon: 'table',
  component: ExcelWidget,
  defaultSize: { width: 6, height: 12 },
  minSize: { width: 4, height: 6 },
  hideShellTitleBar: false,
  hideShellRemoveButton: false,
});
